import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { parse } from 'querystringify'
import { useSelector } from 'react-redux'

// Components
import { Button, Image, Section, IconPlayTransparent } from '@tracklib/kit'
import VideoModal from 'components/modal/video'
import CoverAnimation from 'components/coveranimation'
import PartnerAnimation from 'components/partneranimation'
import SEO from 'components/base/seo'

// Hooks
import { useTracking } from 'modules/tracking/hooks'
import { useDarkMode } from 'modules/profile/hooks'
import { useSignupCode } from 'modules/signup/hooks'

// Utils
import type { State } from 'types'

import styles from './hero2021.css'

type Image = {
  image: number
  alt: string
}

type Hero2021Props = {
  value: {
    title: string
    description: string
    button_text?: string
    button_url?: string
    play_text?: string
    video_url?: string
    covers: Array<Image>
    waveforms: Array<Image>
    background_image?: number
    background_image_dark_mode?: number
  }
}

const Hero2021 = ({
  value: {
    title,
    description,
    play_text,
    video_url,
    button_url,
    button_text,
    covers,
    waveforms,
    background_image,
    background_image_dark_mode,
  },
}: Hero2021Props): JSX.Element => {
  const trackEvent = useTracking()
  const [modalIsOpen, setModalOpen] = useState(false)
  const { edit } = useSelector<State>(state => parse(state.router.location.search)) as {
    edit: string
  }
  const pathname = useSelector<State, string>(state => state.router.location.pathname)
  const isDarkMode = useDarkMode()
  const { reftitle, reftext, image2, image, refcode, refcodevalid } = useSignupCode()
  const openModal = () => {
    setModalOpen(true)
    trackEvent('Play hero video')
  }

  useEffect(() => {
    if (edit) {
      window.addEventListener('click', event => event.preventDefault(), false)
      window.addEventListener('click', event => event.stopPropagation(), true)
      document.designMode = 'on'
    }
  }, [edit])

  const bg = isDarkMode && background_image_dark_mode ? background_image_dark_mode : background_image

  const hasCode = refcode && refcode !== 'default' && refcodevalid && pathname !== '/'

  return (
    <>
      {hasCode && <SEO title={reftitle} description={reftext} image={image} />}
      <div className={styles.bg} style={bg ? { backgroundImage: `url(${bg})` } : {}}>
        <Section extraPad className={styles.wrapper} contentClassName={styles.content}>
          {image2 ? <PartnerAnimation image={image2} /> : <CoverAnimation covers={covers} waveforms={waveforms} />}
          <motion.div className={styles.copy} animate={{ opacity: 1 }} initial={{ opacity: 0 }} transition={{ duration: 1 }}>
            <h1 className={styles.title} datatesting="heroblock_title">
              {hasCode && reftitle ? reftitle : title}
            </h1>
            {description && (
              <p className={styles.description} dangerouslySetInnerHTML={{ __html: hasCode && reftext ? reftext : description }} />
            )}
            {(button_url || video_url) && (
              <div className={styles.buttons}>
                {button_text && (
                  <Button
                    size="large"
                    className={styles.signupButton}
                    to={button_url}
                    datatesting="frontpageherobutton"
                    onClick={() => trackEvent('Click hero button')}
                  >
                    {button_text}
                  </Button>
                )}
                {video_url && (
                  <Button
                    variant="transparent"
                    size="large"
                    className={styles.playButtonContainer}
                    onClick={openModal}
                    datatesting="heroblock_playbutton"
                  >
                    <IconPlayTransparent />
                    {play_text}
                  </Button>
                )}
              </div>
            )}
          </motion.div>
          {video_url && <VideoModal videoUrl={video_url} isOpen={modalIsOpen} closeModal={() => setModalOpen(false)} />}
        </Section>
      </div>
    </>
  )
}

export default Hero2021
